import React, { useEffect, useMemo, useState } from 'react';
import {Button, Table, TableBody, TableContainer, TableHead, TableRow, Paper, Grid, Box, Tooltip, TablePagination, Typography, Rating, TextField, InputAdornment, Menu, MenuItem,Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CreateNewFolderRoundedIcon from '@mui/icons-material/CreateNewFolderRounded';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined'; 
import { StyledTableCell, StyledTableRow } from '../../ui-components/StyledTables';
import { iDataProyectoPorUsuario } from '../../interfaces/iDataProyectoPorUsuario';
import { useProjectsContext } from '../../context/Projects/ProjectContext';
import { ummiapps } from '../../data/ummiapps';
import editIcon from "./icons/editIcon.svg";
import deleteIcon from "./icons/deleteIcon.svg";
import DeleteModal from './DeleteModal';
import { useAuthContext } from '../../hooks/Auth';
import { useActividadesContext } from '../UmmIACategories/Hook/useActividades';
import { useChatUI } from '../UmmIAChatAI/ChatUIContext';
import { keyframes } from '@mui/system';

interface iPropsProyectos {
  handleVerProyecto: (data: iDataProyectoPorUsuario) => void;
}

const overlayFade = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`;

const fadeOutline = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`;

function TablaPlanificacionesPorUsuario({ handleVerProyecto }: iPropsProyectos) {
  const { projects, handleDescargarProyecto, assignToDelete } = useProjectsContext();
  const [vistaEdicion, setVistaEdicion] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [selectedProposal, setSelectedProposal] = useState<string | null>(null);
  const { openChat } = useChatUI();

  const { authMode } = useAuthContext();
  const { cargando, handleUploadToSchoology } = useActividadesContext();

  /** === Carga === */
  useEffect(() => {
    setIsLoading(projects.length === 0);
  }, [projects]);

  /** ID del proyecto más reciente por fechaCreacion  */
  const latestProjectId = useMemo(() => {
    if (!projects || projects.length === 0) return null;
    const latest = projects.reduce((acc, cur) => {
      const a = new Date(acc.fechaCreacion).getTime();
      const b = new Date(cur.fechaCreacion).getTime();
      return b > a ? cur : acc;
    });
    return latest.proyectoId;
  }, [projects]);

  const handleOpenMenu = (
    e: React.MouseEvent<HTMLButtonElement>,
    job: iDataProyectoPorUsuario
  ) => {
    setAnchorEl(e.currentTarget);
    setSelectedProposal(job.propuesta);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedProposal(null);
  };

  const handleChangePage = (_event: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSearchChange = (event: any) => {
    setSearchQuery(event.target.value.toLowerCase());
    setPage(0);
  };

  const sortedData = () => {
    return [...projects].sort((a, b) => {
      if (orderBy === 'fechaCreacion') {
        return order === 'asc'
          ? new Date(a.fechaCreacion).getTime() - new Date(b.fechaCreacion).getTime()
          : new Date(b.fechaCreacion).getTime() - new Date(a.fechaCreacion).getTime();
      }
      if (orderBy === 'titulo' || orderBy === 'nivel' || orderBy === 'tipoEstructura') {
        return order === 'asc'
          ? a[orderBy].localeCompare(b[orderBy])
          : b[orderBy].localeCompare(a[orderBy]);
      }
      if (orderBy === 'calificacion') {
        return order === 'asc' ? a.calificacion - b.calificacion : b.calificacion - a.calificacion;
      }
      // Por defecto, esto retorna el más recientes primero
      return new Date(b.fechaCreacion).getTime() - new Date(a.fechaCreacion).getTime();
    });
  };

  const filteredData = () => {
    return sortedData().filter(
      job =>
        job.titulo?.toLowerCase().includes(searchQuery) ||
        job.nivel?.toLowerCase().includes(searchQuery) ||
        job.tipoEstructura?.toLowerCase().includes(searchQuery)
    );
  };

  const getNombreApp = (tipoEstructura: string): string => {
    const componente = ummiapps.find((app: any) => app.tipoEstructura === tipoEstructura);
    return componente?.nombre || tipoEstructura;
  };

  return (
    <>
      {!vistaEdicion && !isLoading && (
        <>
          <Box sx={{ mt: 2, mb: 4 }}>
            <Typography variant="h4" fontFamily={'Montserrat'} fontWeight={'Bold'} color={'#336B87'}>
              Mis recursos de aprendizaje
            </Typography>
            <TextField
              sx={{ mt: 2 }}
              fullWidth
              id="search"
              label="Buscar..."
              variant="outlined"
              size="small"
              onChange={handleSearchChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Box>
          <Grid container>
            <Grid item xs={12}>
              <Box sx={{ width: '100%', mb: 10 }}>
                <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <Table aria-label="proyectos guardados">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell align="left">N°</StyledTableCell>
                        {['titulo', 'nivel', 'tipoEstructura', 'fechaCreacion', 'calificacion'].map(column => (
                          <StyledTableCell
                            key={column}
                            align="left"
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleRequestSort(column)}
                          >
                            {column === 'titulo' && 'Título'}
                            {column === 'nivel' && 'Nivel'}
                            {column === 'tipoEstructura' && 'Ummia APP'}
                            {column === 'fechaCreacion' && 'Fecha'}
                            {column === 'calificacion' && 'Valoración'}
                            {orderBy === column ? (order === 'asc' ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />) : null}
                          </StyledTableCell>
                        ))}
                        <StyledTableCell align="center">Acciones</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredData()
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((job, index) => {
                          const isLatest = latestProjectId === job.proyectoId;

                          return (
                            <StyledTableRow
                              key={job.proyectoId}
                              sx={{
                                ...(isLatest && {
                                  '& td:first-of-type': {
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&::before': {
                                      content: '""',
                                      position: 'absolute',
                                      inset: 3,
                                      borderRadius: 6,
                                      background: 'rgba(76, 175, 80, 0.28)',
                                      pointerEvents: 'none',
                                      opacity: 1,
                                      '@media (prefers-reduced-motion: no-preference)': {
                                        animation: `${overlayFade} 1600ms ease-out forwards`,
                                      },
                                    },
                                    '&::after': {
                                      content: '""',
                                      position: 'absolute',
                                      inset: 3,
                                      borderRadius: 6,
                                      pointerEvents: 'none',
                                      opacity: 0,
                                      '@media (prefers-reduced-motion: no-preference)': {
                                        animation: `${fadeOutline} 1000ms ease-out 1`,
                                      },
                                    },
                                  },
                                }),
                              }}
                            >
                              {/*  de NUEVO */}
                              <StyledTableCell align="left">
                                {index + 1 + page * rowsPerPage}
                                {isLatest && (
                                  <Chip
                                    icon={<NewReleasesOutlinedIcon />}
                                    label="Nuevo"
                                    size="small"
                                    aria-label="Último recurso creado"
                                    sx={{
                                      ml: 2,
                                      height: 22,
                                      fontSize: '0.70rem',
                                      fontFamily: 'Montserrat',
                                      bgcolor: 'rgba(76,175,80,0.12)', 
                                      color: '#2E7D32',
                                      '& .MuiChip-icon': { fontSize: 15, color: '#2E7D32' },
                                      borderRadius: 1.5,
                                    }}
                                  />
                                )}
                              </StyledTableCell>

                              <StyledTableCell align="left">{job.titulo}</StyledTableCell>
                              <StyledTableCell align="left">{job.nivel}</StyledTableCell>
                              <StyledTableCell align="left">{getNombreApp(job.tipoEstructura)}</StyledTableCell>
                              <StyledTableCell align="left">{new Date(job.fechaCreacion).toLocaleString()}</StyledTableCell>
                              <StyledTableCell align="center">
                                <Rating name="read-only" value={job.calificacion} readOnly />
                              </StyledTableCell>
                              <StyledTableCell align="center">
                                <Tooltip title="Editor"
                                  componentsProps={{
                                    tooltip: {
                                      sx: {
                                        backgroundColor: "#1D3D4D",
                                        color: "#FFF",
                                        fontFamily: "Montserrat",
                                        fontSize: "0.875rem",
                                        padding: "8px 12px",
                                        borderRadius: "8px",
                                      },
                                    },
                                    arrow: { sx: { color: "#1D3D4D" } },
                                  }}>
                                  <Button
                                    onClick={() => { openChat(); handleVerProyecto(job); }}
                                    variant="text"
                                    sx={{ minWidth: 0, p: 0.5, color: '#336B87' }}
                                  >
                                    <Box component="img" src={editIcon} alt="Editar" sx={{ width: 28, height: 28 }} />
                                  </Button>
                                </Tooltip>

                                <>
                                  <Tooltip
                                    title="Exportar / Compartir"
                                    arrow
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          backgroundColor: "#1D3D4D",
                                          color: "#FFF",
                                          fontFamily: "Montserrat",
                                          fontSize: "0.875rem",
                                          padding: "8px 12px",
                                          borderRadius: "8px",
                                        },
                                      },
                                      arrow: { sx: { color: "#1D3D4D" } },
                                    }}
                                  >
                                    <Button
                                      variant="text"
                                      disabled={cargando}
                                      onClick={(e) => handleOpenMenu(e, job)}
                                      sx={{ borderRadius: 5, color: '#336B87' }}
                                    >
                                      <CreateNewFolderRoundedIcon sx={{ fontSize: 25 }} />
                                    </Button>
                                  </Tooltip>

                                  <Menu
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleClose}
                                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                                    transformOrigin={{ vertical: "bottom", horizontal: "center" }}
                                    elevation={0}
                                    sx={{ fontFamily: "Montserrat" }}
                                    PaperProps={{
                                      sx: {
                                        bgcolor: "#1D3D4D",
                                        borderRadius: 5,
                                        fontFamily: "Montserrat",
                                        color: "#FFF"
                                      },
                                    }}
                                  >
                                    <MenuItem
                                      onClick={() => {
                                        if (selectedProposal) {
                                          handleDescargarProyecto(selectedProposal);
                                        }
                                        handleClose();
                                      }}
                                      sx={{
                                        fontFamily: "Montserrat",
                                        transition: "background-color .15s ease, color .15s ease, padding-left .15s ease",
                                        "&:hover": {
                                          bgcolor: "#25485A",
                                          color: "#F8AD0C",
                                          pl: 2.5,
                                        },
                                        "&.Mui-focusVisible": {
                                          bgcolor: "#25485A",
                                          color: "#F8AD0C",
                                        },
                                        position: "relative",
                                        "&:hover::before, &.Mui-focusVisible::before": {
                                          content: '""',
                                          position: "absolute",
                                          left: 0,
                                          top: 6,
                                          bottom: 6,
                                          width: 4,
                                          borderRadius: 2,
                                          backgroundColor: "#F8AD0C",
                                        },
                                      }}>Exportar PDF</MenuItem>
                                    <MenuItem
                                      disabled={authMode !== "LTI"}
                                      sx={{ bgcolor: "#7a7a7a75" }}
                                      onClick={handleUploadToSchoology}
                                    >
                                      Compartir con Schoology
                                    </MenuItem>
                                  </Menu>
                                </>

                                <Tooltip title="Eliminar"
                                  componentsProps={{
                                    tooltip: {
                                      sx: {
                                        backgroundColor: "#1D3D4D",
                                        color: "#FFF",
                                        fontFamily: "Montserrat",
                                        fontSize: "0.875rem",
                                        padding: "8px 12px",
                                        borderRadius: "8px",
                                      },
                                    },
                                    arrow: { sx: { color: "#1D3D4D" } },
                                  }}>
                                  <Button
                                    onClick={() => assignToDelete(job.proyectoId)}
                                    variant="text"
                                    sx={{ minWidth: 0, p: 0.5, color: '#336B87' }}
                                  >
                                    <Box component="img" src={deleteIcon} alt="Eliminar" sx={{ width: 28, height: 28 }} />
                                  </Button>
                                </Tooltip>
                              </StyledTableCell>
                            </StyledTableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  showFirstButton
                  showLastButton
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={projects.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Box>
            </Grid>
          </Grid>
          <Grid item>
            <DeleteModal />
          </Grid>
        </>
      )}
    </>
  );
}

export default TablaPlanificacionesPorUsuario;
